import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  subtitlesApi,
  type CreateProviderBody,
  type HiPreference,
  type OpenSubtitlesConfig,
  type SubtitleLanguageEntry,
  type SubtitleLanguagesProfile,
  type SubtitleSettings,
  type SubtitleProviderRow,
  type SweepStatus,
  type UpdateSubtitleSettings,
} from "@/api/subtitles";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field, NumberInput, Select, TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

const PROVIDER_KINDS = [
  { value: "opensubtitles.com", label: "OpenSubtitles.com" },
] as const;
type ProviderKind = (typeof PROVIDER_KINDS)[number]["value"];

// ---------------------------------------------------------------------------
// Types + helpers
// ---------------------------------------------------------------------------

type TabKey = "languages" | "embedded" | "providers" | "general";

const HI_LABELS: Record<HiPreference, string> = {
  required: "Required (SDH only)",
  excluded: "Excluded (skip SDH)",
  prefer: "Prefer (allow either, SDH preferred)",
};

const blankEntry = (): SubtitleLanguageEntry => ({
  language: "en",
  required: false,
  hi: "prefer",
});

const sameProfile = (
  a: SubtitleLanguagesProfile,
  b: SubtitleLanguagesProfile,
) => JSON.stringify(a) === JSON.stringify(b);

const sameSettings = (a: SubtitleSettings, b: SubtitleSettings) =>
  sameProfile(a.movie, b.movie) &&
  sameProfile(a.tv, b.tv) &&
  a.cutoff_score === b.cutoff_score &&
  a.upgrade_allowed === b.upgrade_allowed &&
  a.auto_search_on_import === b.auto_search_on_import &&
  a.strip_embedded_on_import === b.strip_embedded_on_import &&
  a.keep_untagged_embedded === b.keep_untagged_embedded;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/**
 * Settings UI for the subtitle subsystem (#79). Three-tabbed page
 * mirroring the NZB Downloading page's pattern:
 *
 *   1. Languages — per-media-kind language preference list.
 *   2. Providers — registered subtitle providers (empty until #82
 *      ships OpenSubtitles; the table is wired but with no rows it
 *      renders an EmptyState pointing at the milestone).
 *   3. General  — cutoff score + upgrade-allowed toggle.
 *
 * The Languages + General tabs both write through to
 * `/api/v1/settings/subtitles`. Providers go to
 * `/api/v1/subtitle-providers`.
 */
export function Subtitles() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const settingsQ = useQuery({
    queryKey: ["subtitles", "settings"],
    queryFn: subtitlesApi.settings.get,
  });
  const providersQ = useQuery({
    queryKey: ["subtitles", "providers"],
    queryFn: subtitlesApi.providers.list,
  });

  const [activeTab, setActiveTab] = useState<TabKey>("languages");
  const [draft, setDraft] = useState<SubtitleSettings | null>(null);
  // Most-recently-loaded server value. We use this both as the "pristine"
  // snapshot for the dirty-check, and as the source-of-truth on Discard.
  const loadedRef = useRef<SubtitleSettings | null>(null);

  // Re-seed draft when the query lands, but don't clobber unsaved
  // edits — same dance as `NzbDownloading.tsx` does for general
  // settings: only re-seed when nothing dirty.
  useEffect(() => {
    if (!settingsQ.data) return;
    const dirtyNow =
      !!draft && !!loadedRef.current && !sameSettings(draft, loadedRef.current);
    if (dirtyNow) return;
    setDraft(settingsQ.data);
    loadedRef.current = settingsQ.data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQ.data]);

  const dirty = useMemo(
    () =>
      !!draft && !!loadedRef.current && !sameSettings(draft, loadedRef.current),
    [draft],
  );

  const saveMut = useMutation({
    mutationFn: (body: UpdateSubtitleSettings) =>
      subtitlesApi.settings.update(body),
    onSuccess: (next) => {
      toast("Subtitle settings saved", "success");
      setDraft(next);
      loadedRef.current = next;
      qc.invalidateQueries({ queryKey: ["subtitles", "settings"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Save failed", "error"),
  });

  const discard = () => {
    if (!dirty) return;
    if (!confirm("Discard pending subtitle setting changes?")) return;
    if (loadedRef.current) setDraft(loadedRef.current);
  };

  const save = () => {
    if (!draft || !loadedRef.current) return;
    const prev = loadedRef.current;
    // Send only the fields the user actually changed. Saves a couple of
    // bytes on the wire; more importantly avoids overwriting concurrent
    // edits on fields the user didn't touch.
    const body: UpdateSubtitleSettings = {
      movie: sameProfile(draft.movie, prev.movie) ? undefined : draft.movie,
      tv: sameProfile(draft.tv, prev.tv) ? undefined : draft.tv,
      cutoff_score:
        draft.cutoff_score === prev.cutoff_score ? undefined : draft.cutoff_score,
      upgrade_allowed:
        draft.upgrade_allowed === prev.upgrade_allowed
          ? undefined
          : draft.upgrade_allowed,
      auto_search_on_import:
        draft.auto_search_on_import === prev.auto_search_on_import
          ? undefined
          : draft.auto_search_on_import,
      strip_embedded_on_import:
        draft.strip_embedded_on_import === prev.strip_embedded_on_import
          ? undefined
          : draft.strip_embedded_on_import,
      keep_untagged_embedded:
        draft.keep_untagged_embedded === prev.keep_untagged_embedded
          ? undefined
          : draft.keep_untagged_embedded,
    };
    saveMut.mutate(body);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Subtitles"
        description="Per-media-kind subtitle languages, provider configuration, and global download behaviour."
        actions={
          <SaveBar
            dirty={dirty}
            saving={saveMut.isPending}
            onSave={save}
            onDiscard={discard}
          />
        }
      />

      <Tabs active={activeTab} onChange={setActiveTab} />

      {activeTab === "languages" && (
        <LanguagesTab
          draft={draft}
          setDraft={setDraft}
          loading={settingsQ.isLoading}
          error={settingsQ.isError ? settingsQ.error : null}
        />
      )}
      {activeTab === "embedded" && (
        <EmbeddedTab
          draft={draft}
          setDraft={setDraft}
          dirty={dirty}
          loading={settingsQ.isLoading}
          error={settingsQ.isError ? settingsQ.error : null}
        />
      )}
      {activeTab === "providers" && (
        <ProvidersTab
          rows={providersQ.data ?? []}
          loading={providersQ.isLoading}
          error={providersQ.isError ? providersQ.error : null}
        />
      )}
      {activeTab === "general" && (
        <GeneralTab
          draft={draft}
          setDraft={setDraft}
          loading={settingsQ.isLoading}
          error={settingsQ.isError ? settingsQ.error : null}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SaveBar / Tabs
// ---------------------------------------------------------------------------

function SaveBar({
  dirty,
  saving,
  onSave,
  onDiscard,
}: {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      {dirty && (
        <Button variant="ghost" size="md" onClick={onDiscard} disabled={saving}>
          Discard
        </Button>
      )}
      <Button onClick={onSave} loading={saving} disabled={!dirty}>
        {dirty ? "Save changes" : "Save"}
      </Button>
    </div>
  );
}

function Tabs({
  active,
  onChange,
}: {
  active: TabKey;
  onChange: (k: TabKey) => void;
}) {
  const tabs: { key: TabKey; label: string }[] = [
    { key: "languages", label: "Languages" },
    { key: "embedded", label: "Embedded tracks" },
    { key: "providers", label: "Providers" },
    { key: "general", label: "General" },
  ];
  return (
    <div className="flex gap-1 border-b border-line">
      {tabs.map((t) => {
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={cn(
              "-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm transition",
              isActive
                ? "border-accent font-medium text-foreground"
                : "border-transparent text-foreground/60 hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EmbeddedTab
// ---------------------------------------------------------------------------

const formatBytes = (n: number) => {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.round(n / 1024)} KB`;
};

/**
 * Embedded subtitle tracks — the ones muxed into the video by the
 * release group. Two settings (buffered behind the page's Save, like
 * every other tab) plus the one-off library clean-up, which acts
 * immediately and reports progress by polling.
 */
function EmbeddedTab({
  draft,
  setDraft,
  dirty,
  loading,
  error,
}: {
  draft: SubtitleSettings | null;
  setDraft: React.Dispatch<React.SetStateAction<SubtitleSettings | null>>;
  dirty: boolean;
  loading: boolean;
  error: unknown;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const sweepQ = useQuery({
    queryKey: ["subtitles", "embedded-sweep"],
    queryFn: subtitlesApi.embeddedSweep.status,
    refetchInterval: (q) => (q.state.data?.running ? 1500 : false),
  });
  const onSweep = (next: SweepStatus) =>
    qc.setQueryData(["subtitles", "embedded-sweep"], next);
  const onSweepError = (e: unknown) =>
    toast(e instanceof Error ? e.message : "Clean-up failed", "error");
  const startMut = useMutation({
    mutationFn: (dryRun: boolean) => subtitlesApi.embeddedSweep.start(dryRun),
    onSuccess: onSweep,
    onError: onSweepError,
  });
  const cancelMut = useMutation({
    mutationFn: subtitlesApi.embeddedSweep.cancel,
    onSuccess: onSweep,
    onError: onSweepError,
  });

  if (loading) return <p className="text-sm text-foreground/60">Loading…</p>;
  if (error)
    return (
      <EmptyState
        title="Couldn't load subtitle settings"
        description={
          error instanceof Error ? `Backend said: ${error.message}` : undefined
        }
      />
    );
  if (!draft) return null;

  const sweep = sweepQ.data;
  const running = !!sweep?.running;
  const toolsMissing = sweep ? !sweep.tools_available : false;
  const keepList = (p: SubtitleLanguagesProfile) =>
    p.languages.map((l) => l.language).join(", ") || "none — nothing is removed";

  const clean = () => {
    if (
      !confirm(
        "Rewrite every library file that has unwanted subtitle tracks? Video and audio are copied untouched, but each affected file is rewritten once and this can take hours on a large library.",
      )
    )
      return;
    startMut.mutate(false);
  };

  return (
    <section className="max-w-3xl space-y-6">
      <p className="text-sm text-foreground/60">
        Releases often ship with dozens of subtitle tracks built into the
        video. These settings remove the ones you don't want, keeping the
        languages on the Languages tab (movies: {keepList(draft.movie)}; TV:{" "}
        {keepList(draft.tv)}). Video and audio are copied untouched. A file
        where no track matches is left as it is, so nothing ever ends up with
        no subtitles. Only .mkv files are processed.
      </p>

      <div className="max-w-xl space-y-4">
        <Field
          label="Remove unwanted tracks on import"
          hint="After each movie or episode is imported, rewrite the file without the subtitle tracks in other languages. Adds one full copy of the file to every import."
        >
          <Select
            value={draft.strip_embedded_on_import ? "1" : "0"}
            onChange={(e) =>
              setDraft((d) =>
                d
                  ? { ...d, strip_embedded_on_import: e.target.value === "1" }
                  : d,
              )
            }
          >
            <option value="0">No — leave imported files as they are</option>
            <option value="1">Yes — keep only my languages</option>
          </Select>
        </Field>
        <Field
          label="Tracks with no language tag"
          hint="Some releases don't label their subtitle tracks. Keeping them is the safe choice — the unlabelled track is often the only English one."
        >
          <Select
            value={draft.keep_untagged_embedded ? "1" : "0"}
            onChange={(e) =>
              setDraft((d) =>
                d ? { ...d, keep_untagged_embedded: e.target.value === "1" } : d,
              )
            }
          >
            <option value="1">Keep</option>
            <option value="0">Remove</option>
          </Select>
        </Field>
      </div>

      <div className="space-y-3 rounded-md border border-line bg-surface/30 p-4">
        <div>
          <h3 className="text-sm font-medium">Clean existing library</h3>
          <p className="mt-1 text-xs text-foreground/55">
            Apply the same rule to files already in the library. Preview only
            reads the files and lists what would be removed.
          </p>
        </div>
        {toolsMissing && (
          <p className="text-xs text-bad">
            ffmpeg isn't installed on the server, so tracks can't be removed.
          </p>
        )}
        {dirty && (
          <p className="text-xs text-foreground/55">
            Save your changes first — the clean-up uses the saved settings.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => startMut.mutate(true)}
            disabled={running || toolsMissing || dirty}
            loading={startMut.isPending && startMut.variables === true}
          >
            Preview
          </Button>
          <Button
            onClick={clean}
            disabled={running || toolsMissing || dirty}
            loading={startMut.isPending && startMut.variables === false}
          >
            Clean library
          </Button>
          {running && (
            <Button
              variant="ghost"
              onClick={() => cancelMut.mutate()}
              loading={cancelMut.isPending}
            >
              Stop
            </Button>
          )}
        </div>
        {sweep?.started_at && <SweepReport sweep={sweep} />}
      </div>

      <p className="text-xs text-foreground/40">
        The two settings are buffered — click Save in the page header to
        commit.
      </p>
    </section>
  );
}

function SweepReport({ sweep }: { sweep: SweepStatus }) {
  const verb = sweep.dry_run ? "would be changed" : "changed";
  const headline = sweep.running
    ? `${sweep.dry_run ? "Previewing" : "Cleaning"} — ${sweep.processed} of ${sweep.total} files`
    : `${sweep.dry_run ? "Preview" : "Clean-up"} ${sweep.cancelled ? "stopped" : "finished"} — ${sweep.processed} of ${sweep.total} files checked`;
  return (
    <div className="space-y-2 border-t border-line/60 pt-3 text-xs">
      <p className="font-medium text-foreground/80">{headline}</p>
      <p className="text-foreground/60">
        {sweep.changed} {sweep.changed === 1 ? "file" : "files"} {verb},{" "}
        {sweep.tracks_removed} {sweep.tracks_removed === 1 ? "track" : "tracks"}
        {sweep.dry_run ? " to remove" : " removed"}
        {!sweep.dry_run && sweep.bytes_saved > 0
          ? `, ${formatBytes(sweep.bytes_saved)} freed`
          : ""}
        {sweep.no_kept_track > 0
          ? `; ${sweep.no_kept_track} left alone (no track in your languages)`
          : ""}
        {sweep.failed > 0 ? `; ${sweep.failed} failed` : ""}
      </p>
      {sweep.running && sweep.current && (
        <p className="truncate font-mono text-foreground/45" title={sweep.current}>
          {sweep.current}
        </p>
      )}
      {sweep.last_error && (
        <p className="break-words text-bad">Last error: {sweep.last_error}</p>
      )}
      {sweep.examples.length > 0 && (
        <div className="max-h-72 overflow-auto rounded border border-line/60">
          <table className="w-full text-left">
            <thead className="bg-surface-2/50 text-foreground/55">
              <tr>
                <th className="px-2 py-1.5 font-medium">File</th>
                <th className="px-2 py-1.5 font-medium">
                  {sweep.dry_run ? "Would remove" : "Removed"}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">Kept</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {sweep.examples.map((ex) => (
                <tr key={ex.path}>
                  <td className="max-w-xs truncate px-2 py-1.5" title={ex.path}>
                    {ex.path.split("/").pop()}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-foreground/60">
                    {ex.removed.length} · {ex.removed.join(" ")}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {ex.kept}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sweep.changed > sweep.examples.length && (
            <p className="px-2 py-1.5 text-foreground/45">
              Showing the first {sweep.examples.length} of {sweep.changed}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LanguagesTab
// ---------------------------------------------------------------------------

function LanguagesTab({
  draft,
  setDraft,
  loading,
  error,
}: {
  draft: SubtitleSettings | null;
  setDraft: React.Dispatch<React.SetStateAction<SubtitleSettings | null>>;
  loading: boolean;
  error: unknown;
}) {
  if (loading) return <p className="text-sm text-foreground/60">Loading…</p>;
  if (error)
    return (
      <EmptyState
        title="Couldn't load subtitle settings"
        description={
          error instanceof Error ? `Backend said: ${error.message}` : undefined
        }
      />
    );
  if (!draft) return null;

  const setProfile = (
    kind: "movie" | "tv",
    profile: SubtitleLanguagesProfile,
  ) => setDraft((d) => (d ? { ...d, [kind]: profile } : d));

  return (
    <section className="space-y-8">
      <ProfileEditor
        title="Movies"
        description="Languages tried for every monitored movie. Order = preference."
        profile={draft.movie}
        onChange={(p) => setProfile("movie", p)}
      />
      <ProfileEditor
        title="TV"
        description="Languages tried for every monitored episode. Order = preference."
        profile={draft.tv}
        onChange={(p) => setProfile("tv", p)}
      />
    </section>
  );
}

function ProfileEditor({
  title,
  description,
  profile,
  onChange,
}: {
  title: string;
  description: string;
  profile: SubtitleLanguagesProfile;
  onChange: (p: SubtitleLanguagesProfile) => void;
}) {
  const update = (idx: number, patch: Partial<SubtitleLanguageEntry>) => {
    const next = profile.languages.map((entry, i) =>
      i === idx ? { ...entry, ...patch } : entry,
    );
    onChange({ languages: next });
  };
  const remove = (idx: number) => {
    onChange({ languages: profile.languages.filter((_, i) => i !== idx) });
  };
  const add = () => {
    onChange({ languages: [...profile.languages, blankEntry()] });
  };
  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= profile.languages.length) return;
    const next = [...profile.languages];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange({ languages: next });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground/85">{title}</h3>
          <p className="mt-1 max-w-2xl text-xs text-foreground/55">
            {description}
          </p>
        </div>
        <Button onClick={add} size="sm" variant="secondary">
          Add language
        </Button>
      </div>

      {profile.languages.length === 0 ? (
        <EmptyState
          title="No languages configured"
          description="Add at least one language to enable subtitle search."
          action={<Button onClick={add}>Add language</Button>}
        />
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
              <tr>
                <th className="w-24 px-3 py-2 font-medium">Order</th>
                <th className="px-3 py-2 font-medium">Language</th>
                <th className="px-3 py-2 font-medium">Required</th>
                <th className="px-3 py-2 font-medium">Hearing-impaired</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {profile.languages.map((entry, idx) => (
                <tr
                  key={`${entry.language}-${idx}`}
                  className="border-t border-line"
                >
                  <td className="px-3 py-2 text-xs text-foreground/60">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="rounded border border-line px-1.5 py-0.5 text-foreground/60 hover:text-foreground disabled:opacity-30"
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        aria-label="Move up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="rounded border border-line px-1.5 py-0.5 text-foreground/60 hover:text-foreground disabled:opacity-30"
                        onClick={() => move(idx, 1)}
                        disabled={idx === profile.languages.length - 1}
                        aria-label="Move down"
                      >
                        ↓
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <TextInput
                      value={entry.language}
                      onChange={(e) =>
                        update(idx, { language: e.target.value.trim() })
                      }
                      placeholder="en"
                      className="font-mono"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Select
                      value={entry.required ? "1" : "0"}
                      onChange={(e) =>
                        update(idx, { required: e.target.value === "1" })
                      }
                    >
                      <option value="1">Required</option>
                      <option value="0">Preferred</option>
                    </Select>
                  </td>
                  <td className="px-3 py-2">
                    <Select
                      value={entry.hi}
                      onChange={(e) =>
                        update(idx, { hi: e.target.value as HiPreference })
                      }
                    >
                      {(Object.keys(HI_LABELS) as HiPreference[]).map((k) => (
                        <option key={k} value={k}>
                          {HI_LABELS[k]}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => remove(idx)}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProvidersTab
// ---------------------------------------------------------------------------

function ProvidersTab({
  rows,
  loading,
  error,
}: {
  rows: SubtitleProviderRow[];
  loading: boolean;
  error: unknown;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [adding, setAdding] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);

  const removeMut = useMutation({
    mutationFn: (id: string) => subtitlesApi.providers.remove(id),
    onSuccess: () => {
      toast("Provider removed", "success");
      qc.invalidateQueries({ queryKey: ["subtitles", "providers"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Remove failed", "error"),
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      subtitlesApi.providers.patch(id, { enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subtitles", "providers"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Update failed", "error"),
  });

  const onTest = async (id: string) => {
    setTestingId(id);
    try {
      const res = await subtitlesApi.providers.test(id);
      toast(
        res.ok
          ? `Provider ${id} is healthy`
          : `Provider unhealthy: ${res.error ?? "unknown"}`,
        res.ok ? "success" : "error",
      );
    } catch (err) {
      toast(err instanceof Error ? err.message : "Test failed", "error");
    } finally {
      setTestingId(null);
    }
  };

  if (loading) return <p className="text-sm text-foreground/60">Loading…</p>;
  if (error)
    return (
      <EmptyState
        title="Couldn't load providers"
        description={
          error instanceof Error ? `Backend said: ${error.message}` : undefined
        }
      />
    );

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground/85">
            Subtitle providers
          </h3>
          <p className="mt-1 max-w-2xl text-xs text-foreground/55">
            Configured providers query for subtitles in priority order
            (lower = first). Each provider stores its own credentials.
          </p>
        </div>
        <Button onClick={() => setAdding(true)} size="sm" variant="secondary">
          Add provider
        </Button>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="No subtitle providers configured"
          description="Add OpenSubtitles.com to start fetching subtitles automatically."
          action={
            <Button onClick={() => setAdding(true)}>Add provider</Button>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
              <tr>
                <th className="px-3 py-2 font-medium">Provider</th>
                <th className="px-3 py-2 font-medium">Kind</th>
                <th className="px-3 py-2 font-medium">Enabled</th>
                <th className="px-3 py-2 font-medium">Priority</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-line">
                  <td className="px-3 py-2 font-medium">{row.id}</td>
                  <td className="px-3 py-2 font-mono text-xs text-foreground/60">
                    {row.kind}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <Select
                      value={row.enabled ? "1" : "0"}
                      onChange={(e) =>
                        toggleMut.mutate({
                          id: row.id,
                          enabled: e.target.value === "1",
                        })
                      }
                    >
                      <option value="1">yes</option>
                      <option value="0">no</option>
                    </Select>
                  </td>
                  <td className="px-3 py-2 text-xs tabular-nums text-foreground/70">
                    {row.priority}
                  </td>
                  <td className="space-x-2 px-3 py-2 text-right">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => onTest(row.id)}
                      loading={testingId === row.id}
                    >
                      Test
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        if (
                          confirm(
                            `Remove subtitle provider "${row.id}"? This deletes its config — credentials cannot be recovered.`,
                          )
                        ) {
                          removeMut.mutate(row.id);
                        }
                      }}
                      loading={removeMut.isPending}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddProviderModal open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddProviderModal — currently only opensubtitles.com, but the kind
// dropdown is structured so future providers slot in by appending to
// PROVIDER_KINDS at the top of the file.
// ---------------------------------------------------------------------------

interface AddFormState {
  kind: ProviderKind;
  id: string; // empty = use kind as id
  api_key: string;
  username: string;
  password: string;
}

const blankAddForm = (): AddFormState => ({
  kind: "opensubtitles.com",
  id: "",
  api_key: "",
  username: "",
  password: "",
});

function AddProviderModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<AddFormState>(blankAddForm());
  const [submitting, setSubmitting] = useState(false);

  // Re-seed when the modal reopens — leftover state from a previous
  // half-completed add would be confusing.
  useEffect(() => {
    if (open) setForm(blankAddForm());
  }, [open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (form.kind === "opensubtitles.com" && form.api_key.trim() === "") {
      toast("API key is required", "error");
      return;
    }
    setSubmitting(true);
    try {
      const cfg: OpenSubtitlesConfig = {
        api_key: form.api_key.trim(),
        username: form.username.trim() || null,
        password: form.password ? form.password : null,
      };
      const body: CreateProviderBody = {
        id: form.id.trim() || undefined,
        kind: form.kind,
        enabled: true,
        priority: 0,
        config_json: JSON.stringify(cfg),
      };
      await subtitlesApi.providers.create(body);
      toast("Provider added", "success");
      qc.invalidateQueries({ queryKey: ["subtitles", "providers"] });
      onClose();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Add failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add subtitle provider" width="lg">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Provider">
          <Select
            value={form.kind}
            onChange={(e) =>
              setForm((f) => ({ ...f, kind: e.target.value as ProviderKind }))
            }
          >
            {PROVIDER_KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="ID"
          hint="Stable identifier shown in the providers list. Leave blank to use the kind as the id (recommended unless you run two instances)."
        >
          <TextInput
            value={form.id}
            placeholder={form.kind}
            onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
          />
        </Field>
        {form.kind === "opensubtitles.com" && (
          <>
            <Field
              label="API key"
              hint="Required. Create one at opensubtitles.com/consumers."
            >
              <TextInput
                required
                value={form.api_key}
                onChange={(e) =>
                  setForm((f) => ({ ...f, api_key: e.target.value }))
                }
              />
            </Field>
            <Field
              label="Username"
              hint="Optional. Enables per-account download quota and VIP downloads."
            >
              <TextInput
                value={form.username}
                onChange={(e) =>
                  setForm((f) => ({ ...f, username: e.target.value }))
                }
              />
            </Field>
            <Field label="Password" hint="Optional, paired with username.">
              <TextInput
                type="password"
                value={form.password}
                onChange={(e) =>
                  setForm((f) => ({ ...f, password: e.target.value }))
                }
              />
            </Field>
          </>
        )}
        <div className="flex justify-end gap-2 border-t border-line pt-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            Add
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// GeneralTab
// ---------------------------------------------------------------------------

function GeneralTab({
  draft,
  setDraft,
  loading,
  error,
}: {
  draft: SubtitleSettings | null;
  setDraft: React.Dispatch<React.SetStateAction<SubtitleSettings | null>>;
  loading: boolean;
  error: unknown;
}) {
  if (loading) return <p className="text-sm text-foreground/60">Loading…</p>;
  if (error)
    return (
      <EmptyState
        title="Couldn't load subtitle settings"
        description={
          error instanceof Error ? `Backend said: ${error.message}` : undefined
        }
      />
    );
  if (!draft) return null;

  const setCutoff = (n: number) =>
    setDraft((d) => (d ? { ...d, cutoff_score: n } : d));
  const setUpgrade = (b: boolean) =>
    setDraft((d) => (d ? { ...d, upgrade_allowed: b } : d));
  const setAutoSearch = (b: boolean) =>
    setDraft((d) => (d ? { ...d, auto_search_on_import: b } : d));

  return (
    <section className="max-w-xl space-y-4">
      <Field
        label="Auto-search on import"
        hint="Run the provider chain automatically right after the importer marks a movie or episode as having a file. Leave on unless you've configured providers but want to gate every search behind a manual click on Wanted."
      >
        <Select
          value={draft.auto_search_on_import ? "1" : "0"}
          onChange={(e) => setAutoSearch(e.target.value === "1")}
        >
          <option value="1">Yes — search automatically</option>
          <option value="0">No — manual only</option>
        </Select>
      </Field>
      <Field
        label="Cutoff score"
        hint="Minimum provider score (0-100) to accept a candidate. Higher = stricter; fewer matches but better quality."
      >
        <NumberInput
          min={0}
          max={100}
          value={draft.cutoff_score}
          onChange={(e) =>
            setCutoff(Math.max(0, Math.min(100, Number(e.target.value) || 0)))
          }
        />
      </Field>
      <Field
        label="Upgrade allowed"
        hint="Replace a downloaded subtitle when a higher-score candidate appears later."
      >
        <Select
          value={draft.upgrade_allowed ? "1" : "0"}
          onChange={(e) => setUpgrade(e.target.value === "1")}
        >
          <option value="1">Yes — keep upgrading</option>
          <option value="0">No — leave the first match alone</option>
        </Select>
      </Field>
      <p className="text-xs text-foreground/40">
        Changes are buffered — click Save in the page header to commit.
      </p>
    </section>
  );
}
